import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { Box, Button } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { ActivityButton } from 'components/ActivityButton'
import { usePostPatronBadgeReward } from 'hooks/api/Contests/BadgeReward/usePostPatronBadgeReward'
import { useGetPatronReferralProgressForBadge } from 'hooks/api/Patron/useGetPatronReferralProgressForBadge'
import { DataTable } from 'components/Shared/DataTable'
import { SectionHeader } from 'components/SectionHeader'
import { formatFullDateTime } from 'utils/util'
import GiftIcon from 'assets/gift_empty_state.svg'
import { useSnackbar } from 'stores/useSnackbar'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { type BadgeReward, type PatronBadgeReward } from 'types/api'

const previousBadgesEarnedColumns = (): GridColDef[] => {
  return [
    {
      field: 'createdOn',
      headerName: 'Date Earned',
      minWidth: 120,
      flex: 1.5,
      valueFormatter: (params: { value: string }) => {
        const date = formatFullDateTime(params.value)
        return date
      },
    },
  ]
}

const patronBadgeProgressColumns = (): GridColDef[] => {
  return [
    {
      field: 'patronId',
      headerName: 'Id',
      valueGetter: (params) => params.row.patronId,

      minWidth: 100,
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      valueGetter: (params) => params.row.firstName,
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      valueGetter: (params) => params.row.lastName,
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'email',
      headerName: 'Email',
      valueGetter: (params) => params.row.email,
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      minWidth: 120,
      flex: 1.5,
      valueFormatter: (params: { value: string }) => {
        const date = formatFullDateTime(params.value)
        return date
      },
    },
  ]
}

export const PatronReferralBadgeDetailsModal = ({
  patronId,
  selectedBadgeReward,
  previousBadgesEarned,
  isModalOpen,
  toggleModalIsOpen,
}: {
  patronId: number
  selectedBadgeReward: BadgeReward | null
  previousBadgesEarned: PatronBadgeReward[]
  isModalOpen: boolean
  toggleModalIsOpen: () => void
}) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '80%'
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)

  // query to get the patron's current check-in progress for the selected badge reward
  const patronReferralProgressForBadgeQuery =
    useGetPatronReferralProgressForBadge({
      patronId,
      badgeRewardId: selectedBadgeReward?.id ?? 0,
    })

  const usePostPatronBadgeRewardMutation = usePostPatronBadgeReward({
    patronId,
    badgeRewardId: selectedBadgeReward?.id ?? 0,
    onSuccess: async () => {
      setSnackbarMessage(`Successfully awarded badge to patron`, 'success')
      toggleModalIsOpen()
    },
    onError: () =>
      setSnackbarMessage(`Error: Cannot save Patron badge reward`, 'error'),
  })

  if (patronReferralProgressForBadgeQuery.isPending) {
    return <ActivityIndicator />
  }

  const patronReferralProgressForBadge = (
    patronReferralProgressForBadgeQuery.data ?? []
  )
    .slice() // avoid changing the original array
    .sort(
      (a, b) =>
        new Date(a?.createdOn ?? 0).getTime() -
        new Date(b?.createdOn ?? 0).getTime()
    ) // sort by date
    .slice(0, selectedBadgeReward?.count) // only take the number of check-ins needed to earn the badge

  const handleSave = () => {
    if (!usePostPatronBadgeRewardMutation.isPending) {
      usePostPatronBadgeRewardMutation.mutate({
        patronId,
        badgeRewardId: selectedBadgeReward?.id ?? 0,
      })
    }
  }

  const onModalClose = () => {
    toggleModalIsOpen()
  }

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '65%',
        height: heightModal,
      }}
    >
      <Box>
        <ModalFullHeader
          toggleIsOpen={toggleModalIsOpen}
          title={`${selectedBadgeReward?.name} Badge Details`}
        />
        <ModalFullBody heightModal={heightModal}>
          <form>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ActivityButton
                active={usePostPatronBadgeRewardMutation.isPending}
                type="button"
                variant="contained"
                disabled={!selectedBadgeReward?.id}
                onClick={handleSave}
              >
                Award Badge to Patron
              </ActivityButton>
            </div>
          </form>
          <SectionHeader
            sx={{ paddingTop: 2 }}
            title="Patron Referral Progress For This Badge"
            showButton={false}
          />
          <DataTable
            getRowId={(row) => row.patronId}
            columns={patronBadgeProgressColumns()}
            rows={patronReferralProgressForBadge ?? []}
            noDataMessage="Patron has no current referral progress for this badge."
            emptyDataImageSource={GiftIcon}
          />
          <SectionHeader
            sx={{ paddingTop: 2 }}
            title="Dates Patron Earned This Badge"
            showButton={false}
          />
          <DataTable
            getRowId={(row) => row.id}
            columns={previousBadgesEarnedColumns()}
            rows={previousBadgesEarned}
            sortFieldName="createdOn"
            sort="desc"
            noDataMessage="Patron has not earned this badge previously."
            emptyDataImageSource={GiftIcon}
          />
        </ModalFullBody>
        <ModalFullFooter>
          <Button onClick={() => onModalClose()}>Close</Button>
        </ModalFullFooter>
      </Box>
    </Modal>
  )
}
